import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import SideBar from "../../Common/Layout/Sidebar";
import AccountSettingTabs from "../Master/AccountSettingTabs";
import { useAuth } from "../../Common/Configurations/AuthContext";
import Modal from "react-bootstrap/Modal";
import { Link, useNavigate } from "react-router-dom";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import Login from "../../Authentication/Login";
import currencyFlags from "../../Components/Data/CurrencyFlags.json"; // Import currency flags JSON

const initialFieldValues = {
  vendorId: "00000000-0000-0000-0000-000000000000",
  userId: "00000000-0000-0000-0000-000000000000",
  vendorName: "",
  panNumber: "",
  gstNo: "",
  phoneNumber: "",
  email: "",
  currencyCode: "",
  address: "",
  city: "",
  state: "",
  country: "",
  postalCode: "",
};

const Vendor = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [btnLoading, setBtnLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [values, setValues] = useState(initialFieldValues);
  const [errors, setErrors] = useState({});
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [addShow, setAddShow] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [countries, setCountries] = useState([]);

  // Process countries data from currencyFlags.json
  useEffect(() => {
    if (currencyFlags && Array.isArray(currencyFlags)) {
      const sortedCountries = [...currencyFlags].sort((a, b) =>
        a.country.localeCompare(b.country)
      );
      setCountries(sortedCountries);
    }
  }, []);

  // Get user's default currency and country from auth context
  const getUserDefaultCurrency = () => {
    return user?.currency || "INR";
  };

  const getUserDefaultCountry = () => {
    return user?.country || "IN"; // Default to India if not available
  };

  const handleOpenDeleteModal = (id) => {
    setItemToDelete(id);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleAddClose = () => {
    setAddShow(false);
    setRecordForEdit(null);
  };

  const handleAddShow = () => {
    setRecordForEdit(null);
    // Set default values from user context when opening add modal
    setValues({
      ...initialFieldValues,
      currencyCode: getUserDefaultCurrency(),
      country: getUserDefaultCountry(),
    });
    setAddShow(true);
  };

  useEffect(() => {
    if (recordForEdit !== null) {
      setValues(recordForEdit);
    }
  }, [recordForEdit]);

  const GetData = async (pageNumber) => {
    const res = await apiService.get(
      `${config.GETVENDORBYUSER}/${user?.id}`,
      pageSize,
      pageNumber
    );
    console.log(res);
    setLists(res.data);
    setPageNumber(res.pageNumber);
    setPageSize(res.pageSize);
    setTotalPages(res.totalPages);
    setData(res.data);
    setTotalRecords(res.totalRecords);
  };

  const GetLastPageData = () => {
    GetData(totalPages);
  };

  const GetFirstPageData = () => {
    GetData("1");
  };

  const GetPageData = (number) => {
    setPageNumber(number);
    if (pageNumber !== number) GetData(number);
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const renderPageNumbers = pageNumbers.map((number) => {
    return (
      <li
        className="page-item"
        key={number}
        id={number}
        onClick={() => GetPageData(number)}
      >
        <Link className="page-link">{number}</Link>
      </li>
    );
  });

  const showEditDetails = (data) => {
    setRecordForEdit(data);
    setValues(data);
    setAddShow(true);
  };

  const handleDelete = async () => {
    if (itemToDelete !== null) {
      try {
        await apiService.remove(config.DELETEVENDOR, itemToDelete);
        await GetData(pageNumber);
      } catch (error) {
        // Error toast already handled by apiService
      }
      handleCloseDeleteModal();
    }
  };

  const validate = () => {
    let temp = {};
    temp.vendorName = values.vendorName === "" ? false : true;
    temp.phoneNumber = values.phoneNumber === "" ? false : true;
    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  const toInputUppercase = (e) => {
    e.target.value = ("" + e.target.value).toUpperCase();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setBtnLoading(true);
      var formData = {
        vendorId: values.vendorId,
        userId: user?.id,
        vendorName: values.vendorName,
        panNumber: values.panNumber,
        gstNo: values.gstNo,
        phoneNumber: values.phoneNumber,
        email: values.email,
        currencyCode: values.currencyCode || getUserDefaultCurrency(),
        address: values.address,
        city: values.city,
        state: values.state,
        country: values.country || getUserDefaultCountry(),
        postalCode: values.postalCode,
      };
      try {
        const isNew =
          formData.vendorId === "00000000-0000-0000-0000-000000000000";

        if (isNew) {
          await apiService.create(config.CREATEVENDOR, formData);
        } else {
          await apiService.update(config.UPDATEVENDOR, formData);
        }
        await GetData(pageNumber);
        resetForm();
      } finally {
        setBtnLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const fieldsToUppercase = [
      "vendorName",
      "panNumber",
      "gstNo",
      "currencyCode",
    ];

    // When country changes, update both country and currency
    if (name === "country") {
      const selectedCountry = countries.find(
        (country) => country.countryCode === value
      );
      setValues({
        ...values,
        country: value,
        currencyCode: selectedCountry?.code || getUserDefaultCurrency(),
      });
    } else {
      setValues({
        ...values,
        [name]: fieldsToUppercase.includes(name) ? value.toUpperCase() : value,
      });
    }
  };

  const resetForm = () => {
    setValues({
      ...initialFieldValues,
      currencyCode: getUserDefaultCurrency(),
      country: getUserDefaultCountry(),
    });
    setAddShow(false);
    setBtnLoading(false);
  };

  const applyErrorClass = (field) =>
    field in errors && errors[field] === false ? " form-control-danger" : "";

  useEffect(() => {
    if (user && !loading) {
      GetData(pageNumber);
    }
  }, [user, loading]);

  if (loading) return <p>Loading...</p>;

  if (!user) {
    navigate("/");
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2">
                  <AccountSettingTabs pageName="VENDOR" />
                </div>
                <div className="me-2 pb-1">
                  <div className="row g-4 align-items-center">
                    <div className="col-sm-auto ms-auto">
                      <div className="hstack gap-2">
                        <button
                          type="button"
                          className="btn btn-primary add-btn"
                          onClick={handleAddShow}
                        >
                          <i className="ri-add-line align-bottom me-1" /> Add
                          Vendor
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <div className="table-responsive table-card p-2">
                    <table className="table align-middle" id="customerTable">
                      <thead className="bg-soft-success bg-gradient">
                        <tr>
                          <th>Id</th>
                          <th>Vendor Name</th>
                          <th>PAN Number</th>
                          <th>GST No</th>
                          <th>PhoneNumber</th>
                          <th>Email</th>
                          <th>Address</th>
                          <th>City</th>
                          <th>State</th>
                          <th>Postal Code</th>
                          <th>Country</th>
                          <th>Currency</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody className="list form-check-all">
                        {lists.length > 0 &&
                          lists.map((v, index) => (
                            <tr key={v.vendorId}>
                              <td>{index + 1}</td>
                              <td>{v.vendorName}</td>
                              <td>{v.panNumber}</td>
                              <td>{v.gstNo}</td>
                              <td>{v.phoneNumber}</td>
                              <td>{v.email}</td>
                              <td>{v.address}</td>
                              <td>{v.city}</td>
                              <td>{v.state}</td>
                              <td>{v.postalCode}</td>
                              <td>{v.country}</td>
                              <td>{v.currencyCode}</td>
                              <td>
                                <ul className="list-inline hstack gap-2 mb-0">
                                  <li
                                    className="list-inline-item"
                                    data-bs-toggle="tooltip"
                                    data-bs-trigger="hover"
                                    data-bs-placement="top"
                                    title="Edit"
                                  >
                                    <Link
                                      className="edit-item-btn"
                                      onClick={(e) => showEditDetails(v)}
                                    >
                                      <i className="ri-pencil-fill align-bottom text-muted" />
                                    </Link>
                                  </li>
                                  <li
                                    className="list-inline-item"
                                    data-bs-toggle="tooltip"
                                    data-bs-trigger="hover"
                                    data-bs-placement="top"
                                    title="Delete"
                                  >
                                    <Link
                                      className="remove-item-btn"
                                      onClick={(e) =>
                                        handleOpenDeleteModal(v.vendorId)
                                      }
                                    >
                                      <i className="ri-delete-bin-fill align-bottom text-muted" />
                                    </Link>
                                  </li>
                                </ul>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                    <div className="flex-shrink-0">
                      <div className="text-muted">
                        Showing{" "}
                        <span className="fw-semibold">{lists.length}</span> of{" "}
                        <span className="fw-semibold">{totalRecords}</span>{" "}
                        Results
                      </div>
                    </div>
                    <ul className="pagination pagination-separated pagination-sm mb-0">
                      <li
                        className={
                          "page-item" + data.previousPage === null
                            ? "disabled"
                            : ""
                        }
                        onClick={() => GetFirstPageData()}
                      >
                        <Link className="page-link">Previous</Link>
                      </li>
                      {renderPageNumbers}
                      <li
                        className={
                          "page-item" + data.nextPage === null ? "disabled" : ""
                        }
                        onClick={() => GetLastPageData()}
                      >
                        <Link className="page-link">Next</Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Modal show={addShow} onHide={handleAddClose} size="lg">
          <Modal.Header>
            <Modal.Title>
              {values.vendorId === "00000000-0000-0000-0000-000000000000"
                ? "Add"
                : "Edit"}{" "}
              Vendor
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form onSubmit={handleSubmit} autoComplete="off" noValidate>
              <div className="row">
                {/* Row 1: Vendor Name *, Phone Number *, Email */}
                <div className="col-md-4 mb-3">
                  <label htmlFor="vendorName" className="form-label">
                    Vendor Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    value={values.vendorName}
                    name="vendorName"
                    onChange={handleInputChange}
                    className={"form-control" + applyErrorClass("vendorName")}
                    placeholder="Please Enter Vendor Name"
                    maxLength={45}
                  />
                  {errors.vendorName === false ? (
                    <div className="validationerror">Please enter name </div>
                  ) : (
                    ""
                  )}
                </div>

                <div className="col-md-4 mb-3">
                  <label htmlFor="phoneNumber" className="form-label">
                    Phone Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    value={values.phoneNumber}
                    name="phoneNumber"
                    onChange={handleInputChange}
                    className={"form-control" + applyErrorClass("phoneNumber")}
                    placeholder="Please Enter Phone Number"
                    maxLength={15}
                  />
                  {errors.phoneNumber === false ? (
                    <div className="validationerror">
                      Please enter phone number{" "}
                    </div>
                  ) : (
                    ""
                  )}
                </div>

                <div className="col-md-4 mb-3">
                  <label htmlFor="email" className="form-label">
                    Email
                  </label>
                  <input
                    type="text"
                    value={values.email}
                    name="email"
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Please Enter Email"
                    maxLength={45}
                  />
                </div>

                {/* Row 2: Address, City, State */}
                <div className="col-md-8 mb-3">
                  <label htmlFor="address" className="form-label">
                    Address
                  </label>
                  <input
                    type="text"
                    value={values.address}
                    name="address"
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Please Enter Address"
                    maxLength={250}
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label htmlFor="city" className="form-label">
                    City
                  </label>
                  <input
                    type="text"
                    value={values.city}
                    name="city"
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Please Enter City"
                    maxLength={50}
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label htmlFor="state" className="form-label">
                    State
                  </label>
                  <input
                    type="text"
                    value={values.state}
                    name="state"
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Please Enter State"
                    maxLength={50}
                  />
                </div>

                {/* Row 3: Postal Code, Country, Currency */}
                <div className="col-md-4 mb-3">
                  <label htmlFor="postalCode" className="form-label">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    value={values.postalCode}
                    name="postalCode"
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Please Enter Postal Code"
                    maxLength={10}
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label htmlFor="country" className="form-label">
                    Country
                  </label>
                  <select
                    name="country"
                    value={values.country}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option
                        key={country.countryCode}
                        value={country.countryCode}
                      >
                        {country.flag && (
                          <img
                            src={country.flag}
                            alt={country.country}
                            style={{
                              width: "20px",
                              height: "15px",
                              marginRight: "8px",
                              verticalAlign: "middle",
                            }}
                          />
                        )}
                        {country.country} - {country.countryCode}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4 mb-3">
                  <label htmlFor="currencyCode" className="form-label">
                    Currency
                  </label>
                  <input
                    type="text"
                    value={values.currencyCode}
                    name="currencyCode"
                    className="form-control"
                    placeholder="Currency will auto-fill based on country"
                    readOnly
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label htmlFor="gstNo" className="form-label">
                    GST No
                  </label>
                  <input
                    type="text"
                    value={values.gstNo}
                    name="gstNo"
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Please Enter GST No"
                    maxLength={25}
                  />
                </div>

                {/* Row 4: PAN Number */}
                <div className="col-md-4 mb-3">
                  <label htmlFor="panNumber" className="form-label">
                    PAN Number
                  </label>
                  <input
                    type="text"
                    value={values.panNumber}
                    name="panNumber"
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Please Enter PAN Number"
                    maxLength={10}
                  />
                </div>
              </div>

              <Modal.Footer>
                <div className="hstack gap-2 justify-content-end mb-3 mt-4">
                  {btnLoading === false ? (
                    <button type="submit" className="btn btn-primary">
                      {values.vendorId ===
                      "00000000-0000-0000-0000-000000000000"
                        ? "Submit"
                        : "Update"}
                    </button>
                  ) : (
                    <button type="submit" className="btn btn-primary" disabled>
                      Please wait...
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                </div>
              </Modal.Footer>
            </form>
          </Modal.Body>
        </Modal>
        <DeleteAlert
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          handleDelete={handleDelete}
        />
      </div>
    </div>
  );
};

export default Vendor;
